import {
  sharedActivityFrequencyStep,
  sharedSleepQualityStep,
  sharedWaterIntakeStep,
} from '@/flow/shared/commonQuestions'
import type { Step } from '@/types'

// Helper to check if user selected a specific concern
const hasConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_pele
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasBodyConcern = (concern: string) => (ctx: { vars: Record<string, any> }) => {
  const concerns = ctx.vars.preocupacoes_corpo
  return Array.isArray(concerns) && concerns.includes(concern)
}

/** Objective-specific steps: Skin Care (pele) */
export const skinCareSteps: Step[] = [
  // ========== FACE ==========
  {
    id: 'intro_face',
    type: 'message',
    content: 'Vamos começar falando sobre a pele do seu rosto.',
  },

  // 1. Main face concerns — checkbox
  {
    id: 'skin_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_pele',
    multiple: true,
    minSelections: 1,
    content: 'Quais são suas principais preocupações com a pele do rosto?',
    options: [
      { label: 'Acne / Cravos ', value: 'acne' },
      { label: 'Manchas / Hiperpigmentação', value: 'manchas' },
      { label: 'Poros dilatados / Oleosidade', value: 'poros' },
      { label: 'Rugas / Linhas finas', value: 'rugas' },
      { label: 'Flacidez facial', value: 'flacidez' },
      { label: 'Textura irregular', value: 'textura_irregular' },
      { label: 'Falta de viço / Pele opaca', value: 'falta_de_vico' },
      { label: 'Ressecamento', value: 'ressecamento' },
    ],
  },

  // 2. Acne follow-up — only if selected acne
  {
    id: 'acne_severity',
    type: 'choice',
    inputKey: 'acne_gravidade',
    content: 'Como você descreveria sua acne?',
    condition: hasConcern('acne'),
    options: [
      { label: 'Leve (alguns cravos ou espinhas)', value: 'leve' },
      { label: 'Moderada (várias lesões no rosto)', value: 'moderada' },
      { label: 'Severa (lesões inflamadas e dolorosas)', value: 'severa' },
    ],
  },
  {
    id: 'acne_location',
    type: 'choice',
    inputKey: 'acne_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a acne aparece com mais frequência?',
    condition: hasConcern('acne'),
    options: [
      { label: 'Testa', value: 'testa' },
      { label: 'Nariz', value: 'nariz' },
      { label: 'Bochechas', value: 'bochechas' },
      { label: 'Queixo', value: 'queixo' },
      { label: 'Mandíbula', value: 'mandibula' },
    ],
  },

  // 3. Manchas follow-up — only if selected manchas
  {
    id: 'spots_type',
    type: 'choice',
    inputKey: 'manchas_tipo',
    multiple: true,
    minSelections: 1,
    content: 'Que tipo de manchas você tem?',
    condition: hasConcern('manchas'),
    options: [
      { label: 'Manchas de sol', value: 'sol' },
      { label: 'Manchas de acne (marcas pós-inflamatórias)', value: 'pos_acne' },
      { label: 'Melasma', value: 'melasma' },
      { label: 'Sardas', value: 'sardas' },
      { label: 'Não sei ao certo', value: 'nao_sei' },
    ],
  },

  // 4. Poros follow-up — only if selected poros
  {
    id: 'pores_location',
    type: 'choice',
    inputKey: 'poros_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde os poros dilatados ou oleosidade incomodam mais?',
    condition: hasConcern('poros'),
    options: [
      { label: 'Zona T (testa, nariz, queixo)', value: 'zona_t' },
      { label: 'Bochechas', value: 'bochechas' },
      { label: 'Rosto inteiro', value: 'rosto_inteiro' },
    ],
  },

  // 5. Rugas follow-up — only if selected rugas
  {
    id: 'wrinkles_location',
    type: 'choice',
    inputKey: 'rugas_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde as rugas ou linhas te incomodam mais?',
    condition: hasConcern('rugas'),
    options: [
      { label: 'Testa', value: 'testa' },
      { label: 'Ao redor dos olhos (pés de galinha)', value: 'olhos' },
      { label: 'Entre as sobrancelhas', value: 'glabela' },
      { label: 'Ao redor da boca', value: 'boca' },
    ],
  },

  // 6. Eye area — skippable
  {
    id: 'eye_area_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_area_dos_olhos',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho queixas',
    content: 'E na área dos olhos, o que mais te incomoda?',
    options: [
      { label: 'Rugas / Linhas finas', value: 'rugas_linhas_finas' },
      { label: 'Bolsas', value: 'bolsas' },
      { label: 'Olheiras', value: 'olheiras' },
    ],
  },

  // 7. Skin type
  {
    id: 'skin_type',
    type: 'choice',
    inputKey: 'tipo_de_pele',
    content: 'Qual seu tipo de pele do rosto?',
    options: [
      { label: 'Normal', value: 'normal' },
      { label: 'Seca', value: 'seca' },
      { label: 'Mista', value: 'mista' },
      { label: 'Oleosa', value: 'oleosa' },
    ],
  },

  // 8. Sensitive skin
  {
    id: 'sensitive_skin',
    type: 'choice',
    inputKey: 'pele_sensivel',
    content: 'Você tem pele sensível ou alguma condição dermatológica?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'sensitive_skin_condition',
    type: 'choice',
    inputKey: 'condicao_pele_sensivel',
    multiple: true,
    minSelections: 1,
    content: 'Quais condições você tem?',
    condition: (ctx) => ctx.vars.pele_sensivel === 'sim',
    options: [
      { label: 'Dermatite', value: 'dermatite' },
      { label: 'Rosácea', value: 'rosacea' },
      { label: 'Psoríase', value: 'psoriase' },
      { label: 'Eczema', value: 'eczema' },
      { label: 'Vitiligo', value: 'vitiligo' },
      { label: 'Outra', value: 'outra' },
    ],
  },

  // ========== BODY ==========
  {
    id: 'intro_body',
    type: 'message',
    content: 'Agora vamos falar sobre a pele do seu corpo.',
  },

  // 9. Body concerns — checkbox
  {
    id: 'body_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_corpo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho queixas no corpo',
    content: 'Você tem alguma preocupação com a pele do corpo?',
    options: [
      { label: 'Celulite', value: 'celulite' },
      { label: 'Estrias', value: 'estrias' },
      { label: 'Flacidez corporal', value: 'flacidez_corporal' },
      { label: 'Pele seca / Ressecamento', value: 'pele_seca' },
      { label: 'Acne corporal (costas, peito)', value: 'acne_corporal' },
      { label: 'Manchas no corpo', value: 'manchas_corpo' },
      { label: 'Queratose pilar (bolinhas nos braços)', value: 'queratose_pilar' },
      { label: 'Foliculite (pelos encravados)', value: 'foliculite' },
    ],
  },

  // 10. Celulite follow-up
  {
    id: 'cellulite_location',
    type: 'choice',
    inputKey: 'celulite_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a celulite te incomoda mais?',
    condition: hasBodyConcern('celulite'),
    options: [
      { label: 'Coxas', value: 'coxas' },
      { label: 'Glúteos', value: 'gluteos' },
      { label: 'Barriga', value: 'barriga' },
      { label: 'Braços', value: 'bracos' },
    ],
  },
  {
    id: 'cellulite_severity',
    type: 'choice',
    inputKey: 'celulite_gravidade',
    content: 'Como você classifica sua celulite?',
    condition: hasBodyConcern('celulite'),
    options: [
      { label: 'Leve (só aparece quando aperto)', value: 'leve' },
      { label: 'Moderada (visível em pé)', value: 'moderada' },
      { label: 'Severa (muito visível, com ondulações)', value: 'severa' },
    ],
  },

  // 11. Estrias follow-up
  {
    id: 'stretch_marks_location',
    type: 'choice',
    inputKey: 'estrias_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde estão as estrias?',
    condition: hasBodyConcern('estrias'),
    options: [
      { label: 'Barriga', value: 'barriga' },
      { label: 'Coxas', value: 'coxas' },
      { label: 'Glúteos', value: 'gluteos' },
      { label: 'Seios', value: 'seios' },
      { label: 'Braços', value: 'bracos' },
      { label: 'Costas', value: 'costas' },
    ],
  },
  {
    id: 'stretch_marks_color',
    type: 'choice',
    inputKey: 'estrias_cor',
    content: 'Qual a cor das suas estrias?',
    condition: hasBodyConcern('estrias'),
    options: [
      { label: 'Vermelhas / Roxas (recentes)', value: 'vermelhas' },
      { label: 'Brancas / Prateadas (antigas)', value: 'brancas' },
      { label: 'Tenho dos dois tipos', value: 'ambas' },
    ],
  },

  // 12. Acne corporal follow-up
  {
    id: 'body_acne_location',
    type: 'choice',
    inputKey: 'acne_corporal_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a acne corporal aparece?',
    condition: hasBodyConcern('acne_corporal'),
    options: [
      { label: 'Costas', value: 'costas' },
      { label: 'Peito', value: 'peito' },
      { label: 'Ombros', value: 'ombros' },
      { label: 'Glúteos', value: 'gluteos' },
    ],
  },

  // 13. Foliculite follow-up
  {
    id: 'folliculitis_location',
    type: 'choice',
    inputKey: 'foliculite_localizacao',
    multiple: true,
    minSelections: 1,
    content: 'Onde a foliculite aparece mais?',
    condition: hasBodyConcern('foliculite'),
    options: [
      { label: 'Virilha', value: 'virilha' },
      { label: 'Axilas', value: 'axilas' },
      { label: 'Pernas', value: 'pernas' },
      { label: 'Barba (rosto)', value: 'barba' },
      { label: 'Glúteos', value: 'gluteos' },
    ],
  },

  // ========== NECK & COLO ==========
  {
    id: 'neck_colo_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_pescoco_colo',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho queixas',
    content: 'E na região do pescoço e colo, tem algo que te incomoda?',
    options: [
      { label: 'Rugas / Linhas', value: 'rugas' },
      { label: 'Flacidez', value: 'flacidez' },
      { label: 'Manchas', value: 'manchas' },
      { label: 'Papada', value: 'papada' },
    ],
  },

  // ========== HANDS & FEET ==========
  {
    id: 'hands_feet_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_maos_pes',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não tenho queixas',
    content: 'Você tem alguma preocupação com mãos ou pés?',
    options: [
      { label: 'Mãos ressecadas', value: 'maos_ressecadas' },
      { label: 'Manchas nas mãos', value: 'manchas_maos' },
      { label: 'Pés ressecados / Rachaduras', value: 'pes_ressecados' },
      { label: 'Calosidades', value: 'calosidades' },
    ],
  },

  // ========== SKINCARE ROUTINE ==========
  {
    id: 'intro_routine',
    type: 'message',
    content: 'Agora algumas perguntas sobre seus hábitos de cuidado.',
  },

  {
    id: 'sunscreen',
    type: 'choice',
    inputKey: 'protetor_solar_diario',
    content: 'Você usa protetor solar diariamente?',
    options: [
      { label: 'Sim, sempre', value: 'sim' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'moisturizer',
    type: 'choice',
    inputKey: 'hidratante',
    content: 'Você usa hidratante regularmente?',
    options: [
      { label: 'Sim, rosto e corpo', value: 'sim_ambos' },
      { label: 'Só no rosto', value: 'so_rosto' },
      { label: 'Só no corpo', value: 'so_corpo' },
      { label: 'Não uso', value: 'nao' },
    ],
  },
  {
    id: 'regular_makeup',
    type: 'choice',
    inputKey: 'usa_maquiagem_regularmente',
    content: 'Você usa maquiagem regularmente?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Não', value: 'nao' },
    ],
  },
  {
    id: 'skincare_products',
    type: 'choice',
    inputKey: 'produtos_skincare',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não uso nenhum',
    content: 'Quais produtos você já usa na rotina de skincare?',
    options: [
      { label: 'Sabonete facial', value: 'sabonete_facial' },
      { label: 'Tônico', value: 'tonico' },
      { label: 'Sérum / Vitamina C', value: 'serum' },
      { label: 'Ácidos (retinol, glicólico, etc)', value: 'acidos' },
      { label: 'Antioxidantes', value: 'antioxidantes' },
      { label: 'Anti-idade', value: 'anti_idade' },
    ],
  },

  // ========== LIFESTYLE ==========
  { ...sharedActivityFrequencyStep },
  {
    id: 'sun_exposure',
    type: 'choice',
    inputKey: 'exposicao_sol',
    content: 'Com que frequência você se expõe ao sol?',
    options: [
      { label: 'Diariamente', value: 'diariamente' },
      { label: 'Algumas vezes por semana', value: 'algumas_vezes' },
      { label: 'Raramente', value: 'raramente' },
    ],
  },
  { ...sharedWaterIntakeStep },
  { ...sharedSleepQualityStep },
  {
    id: 'smoking',
    type: 'choice',
    inputKey: 'fumante',
    content: 'Você fuma?',
    options: [
      { label: 'Sim', value: 'sim' },
      { label: 'Não', value: 'nao' },
      { label: 'Parei recentemente', value: 'parou' },
    ],
  },
]
